import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    useTheme,
    useMediaQuery,
  } from "@mui/material";
  import React from "react";
  import { useTranslation } from "react-i18next";
  import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
  import AccessTimeIcon from '@mui/icons-material/AccessTime';
  import DescriptionIcon from '@mui/icons-material/Description';
  import TrackChangesIcon from '@mui/icons-material/TrackChanges';
  import ComputerIcon from '@mui/icons-material/Computer';
  import AssessmentIcon from '@mui/icons-material/Assessment';
  import PTTranslate from "../../../../common/components/ui/PTTranslate";
  import { styled } from '@mui/material/styles';
import { useAuth } from "../../../../common/provider";
  
  // Styled Paper component for the feature boxes
  const FeatureBox = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(3),
    height: '100%',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
    },
  }));
  
  // Styled Box for the icons - changed color to green
  const IconBox = styled(Box)(({ theme }) => ({
    color: '#35b37e', // Changed to green color
    marginRight: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }));
  
  const FeaturesSection = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { toggleInscriptionDialog } = useAuth();
    
    const features = [
      {
        icon: <HourglassEmptyIcon sx={{ fontSize: { xs: 36, md: 48 } }} />,
        titleKey: "features.reducedProcessing.title",
        descriptionKey: "features.reducedProcessing.description"
      },
      {
        icon: <AccessTimeIcon sx={{ fontSize: { xs: 36, md: 48 } }} />,
        titleKey: "features.twentyFourSeven.title",
        descriptionKey: "features.twentyFourSeven.description"
      },
      {
        icon: <DescriptionIcon sx={{ fontSize: { xs: 36, md: 48 } }} />,
        titleKey: "features.secureDocument.title",
        descriptionKey: "features.secureDocument.description"
      },
      {
        icon: <TrackChangesIcon sx={{ fontSize: { xs: 36, md: 48 } }} />,
        titleKey: "features.realTime.title",
        descriptionKey: "features.realTime.description"
      },
      {
        icon: <ComputerIcon sx={{ fontSize: { xs: 36, md: 48 } }} />,
        titleKey: "features.simplifiedApplication.title",
        descriptionKey: "features.simplifiedApplication.description"
      },
      {
        icon: <AssessmentIcon sx={{ fontSize: { xs: 36, md: 48 } }} />,
        titleKey: "features.increasedAccuracy.title",
        descriptionKey: "features.increasedAccuracy.description"
      }
    ];
  
    return (
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          bgcolor: '#f9f9f9',
          width: '100%',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 4, md: 6 }, textAlign: 'center' }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                fontWeight: 600,
                mb: 2,
                color: 'text.primary',
              }}
            >
              <PTTranslate tk="features.title" />
            </Typography>
            
            <Typography
              variant="h3"
              component="h3"
              sx={{
                fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
                fontWeight: 400,
                color: 'text.secondary',
                maxWidth: '800px',
                mx: 'auto',
                mb: { xs: 3, md: 5 },
              }}
            >
              {/* <PTTranslate tk="features.subtitle" /> */}
            </Typography>
          </Box>
  
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureBox>
                  <IconBox>
                    {feature.icon}
                  </IconBox>
                  <Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: { xs: '1rem', md: '1.25rem' },
                      }}
                    >
                      <PTTranslate tk={feature.titleKey} />
                    </Typography>
                    {!isMobile && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: { xs: '0.875rem', md: '1rem' },
                        }}
                      >
                        <PTTranslate tk={feature.descriptionKey} />
                      </Typography>
                    )}
                  </Box>
                </FeatureBox>
              </Grid>
            ))}
          </Grid>
          
          <Box 
            sx={{ 
              textAlign: 'center', 
              mt: { xs: 4, md: 6 } 
            }}
          >
            <Box
            onClick={toggleInscriptionDialog}
              component="button"
              sx={{
                py: { xs: 1.2, md: 1.8 },
                px: { xs: 3, md: 5 },
                fontSize: { xs: '0.9rem', md: '1.1rem' },
                fontWeight: 600,
                bgcolor: '#35b37e',
                color: '#fff',
                border: 'none',
                borderRadius: '30px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#2a8f64',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                },
              }}
            >
              <PTTranslate tk="features.getStarted" />
            </Box>
          </Box>
        </Container>
      </Box>
    );
  };
  
  export default FeaturesSection;